import { useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  BranchSelectionPayload,
  CreateCarePlanPayload,
  CycleModificationMode,
  SaveCarePlanCyclePayload,
  SaveCarePlanStepPayload,
  UpdateCarePlanPayload,
} from "@/types/care-plan";

import { carePlanApi } from "@/api/provider/patient/care-plan";
import { carePlanQueryKeys } from "@/api/provider/patient/care-plan-query";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";

export const useCreateCarePlanMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCarePlanPayload) => carePlanApi.create(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientQueryKeys.sidebarCountsForPatient(variables.patientId),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.all,
      });
    },
  });
};

export const useUpdateCarePlanMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; data: UpdateCarePlanPayload }) =>
      carePlanApi.update(payload.id, payload.data),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(carePlanQueryKeys.detail(variables.id), data);
      }
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.listForPatient(data?.patientId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.logs(variables.id),
      });
    },
  });
};

export const useSaveCarePlanStepsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      carePlanId: string;
      steps: SaveCarePlanStepPayload[];
      cycles?: SaveCarePlanCyclePayload[];
      branchSelections?: BranchSelectionPayload[];
      cycleModificationMode?: CycleModificationMode;
    }) =>
      carePlanApi.saveSteps(payload.carePlanId, payload.steps, {
        cycles: payload.cycles,
        branchSelections: payload.branchSelections,
        cycleModificationMode: payload.cycleModificationMode,
      }),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(
          carePlanQueryKeys.detail(variables.carePlanId),
          data,
        );
      }
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.listForPatient(data?.patientId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.logs(variables.carePlanId),
      });
    },
  });
};

export const useScheduleStepMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      carePlanId: string;
      stepId: string;
      eventId: string;
    }) =>
      carePlanApi.scheduleStep(
        payload.carePlanId,
        payload.stepId,
        payload.eventId,
      ),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(
          carePlanQueryKeys.detail(variables.carePlanId),
          data,
        );
      }
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.listForPatient(data?.patientId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.logs(variables.carePlanId),
      });
    },
  });
};

export const useSelectBranchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      carePlanId: string;
      stepId: string;
      branchId: string;
    }) =>
      carePlanApi.selectBranch(
        payload.carePlanId,
        payload.stepId,
        payload.branchId,
      ),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(
          carePlanQueryKeys.detail(variables.carePlanId),
          data,
        );
      }
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.listForPatient(data?.patientId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.logs(variables.carePlanId),
      });
    },
  });
};

export const useDeleteCarePlanMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => carePlanApi.delete(id),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: carePlanQueryKeys.all,
      });
    },
  });
};
